import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ArrowUpRight, Lock, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageMeta from '../components/PageMeta';
import { getApiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';
import ReportToolbar from '../components/ReportToolbar';
import { ReportIcon } from '../lib/reportIcon';

const ResearchDashboard = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [selectedSector, setSelectedSector] = useState('all-sectors');
  const [selectedType, setSelectedType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [reports, setReports] = useState([]);
  const [subscriptionReports, setSubscriptionReports] = useState([]);
  const [subscriptionReportsError, setSubscriptionReportsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
    max_accessible_pages: 2,
    has_more: false
  });
  const [userRole, setUserRole] = useState('FREE_USER');
  const [subscription, setSubscription] = useState(null);
  const [ctaBilling, setCtaBilling] = useState('monthly');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const API = getApiUrl();
        const resp = await fetch(`${API}/auth/me`, {
          method: 'GET',
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.role) setUserRole(data.role);
        }
      } catch (e) {
        console.error('Failed to fetch user:', e);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const API = getApiUrl();
        const resp = await fetch(`${API}/payments/subscription/current`, {
          method: 'GET',
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          setSubscription(data);
        }
      } catch (e) {
        console.error('Failed to fetch subscription:', e);
      }
    };
    fetchSubscription();
  }, []);


  const daysRemaining = (endDate) => {
    if (!endDate) return null;
    const diff = new Date(endDate) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const sectors = [
    'All Sectors',
    'Agri Inputs / Fertilisers',
    'Auto & Auto Ancillaries',
    'Banking',
    'Capital Goods / Industrials',
    'Cement & Building Materials',
    'Chemicals / Specialty Chemicals',
    'Consumer Durables',
    'Defence / Aerospace',
    'Financial Services / NBFC',
    'FMCG',
    'Hotels / Travel / Tourism',
    'Information Technology',
    'Infrastructure / Construction / EPC',
    'Insurance & Capital Markets',
    'Logistics / Transport Services',
    'Media & Entertainment',
    'Metals & Mining',
    'Oil & Gas / Energy',
    'Pharma & Healthcare',
    'Power / Utilities',
    'Railways / PSU Theme',
    'Real Estate / REITs',
    'Retail / Consumer Services',
    'Telecom',
    'Textiles / Apparel'
  ];

  const reportTypes = [
    'Equity Research',
    'Sector Analysis'
  ];

  const toOptionValue = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const normalizeFilterValue = (value) => (value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

  useEffect(() => {
    const fetchReports = async (page = 1) => {
      try {
        setLoading(true);
        const API = getApiUrl();
        const token = localStorage.getItem('access_token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const resp = await fetch(`${API}/reports?page=${page}`, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
        if (!resp.ok) {
          throw new Error('Failed to fetch reports');
        }
        const data = await resp.json();
        setReports(data.reports || []);
        setPagination({
          total: data.total || 0,
          page: data.page || 1,
          page_size: data.page_size || 10,
          total_pages: data.total_pages || 0,
          max_accessible_pages: data.max_accessible_pages || 2,
          has_more: data.has_more || false
        });
      } catch (err) {
        console.error('Failed to fetch reports:', err);
        setReports([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReports(pagination.page);
  }, [pagination.page]);

  useEffect(() => {
    const fetchSubscriptionReports = async () => {
      try {
        setSubscriptionReportsError('');
        const API = getApiUrl();
        const token = localStorage.getItem('access_token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const url = `${API}/subscription-reports?category=Research`;
        const resp = await fetch(url, {
          method: 'GET',
          headers,
          credentials: 'include',
        });

        if (!resp.ok) {
          console.error('Failed to fetch subscription reports:', resp.status);
          setSubscriptionReportsError(`Failed to load reports (Status: ${resp.status})`);
          setSubscriptionReports([]);
          return;
        }

        const data = await resp.json();
        setSubscriptionReports(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to fetch subscription reports:', e);
        setSubscriptionReportsError('Failed to load reports');
        setSubscriptionReports([]);
      }
    };

    fetchSubscriptionReports();
  }, []);

  const getTagStyle = (color) => {
    const styles = {
      green: 'bg-green-50 text-green-700 border-green-200',
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      red: 'bg-red-50 text-red-700 border-red-200'
    };
    return styles[color] || styles.blue;
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.max_accessible_pages) {
      return;
    }
    setPagination({ ...pagination, page: newPage });
  };

  const filteredSubscriptionReports = subscriptionReports.filter((report) => {
    const selectedSectorLabel = selectedSector === 'all' || selectedSector === 'all-sectors'
      ? 'All Sectors'
      : sectors.find((item) => toOptionValue(item) === selectedSector);
    const selectedTypeLabel = !selectedType
      ? ''
      : reportTypes.find((item) => toOptionValue(item) === selectedType);

    const reportSector = normalizeFilterValue(report.primaryFilter);
    const reportType = normalizeFilterValue(report.secondaryFilter);
    const sectorAllValues = ['allsectors', 'all'];
    const sectorMatches = !selectedSectorLabel || selectedSectorLabel === 'All Sectors'
      || sectorAllValues.includes(reportSector)
      || reportSector === normalizeFilterValue(selectedSectorLabel);
    const typeMatches = !selectedTypeLabel
      || reportType === normalizeFilterValue(selectedTypeLabel);
    const queryMatches = !searchQuery.trim() || [report.title, report.code, report.description, report.category, report.primaryFilter, report.secondaryFilter]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    return sectorMatches && typeMatches && queryMatches;
  });

  const filteredReports = reports.filter((report) => {
    const selectedSectorLabel = selectedSector === 'all' || selectedSector === 'all-sectors'
      ? 'All Sectors'
      : sectors.find((item) => toOptionValue(item) === selectedSector);
    const selectedTypeLabel = !selectedType
      ? ''
      : reportTypes.find((item) => toOptionValue(item) === selectedType);

    const text = [
      report.title,
      report.category,
      report.preview_content,
      ...(report.tags || []),
    ].filter(Boolean).join(' ');

    const sectorMatches = !selectedSectorLabel || selectedSectorLabel === 'All Sectors'
      || text.toLowerCase().includes(selectedSectorLabel.toLowerCase());
    const typeMatches = !selectedTypeLabel || normalizeFilterValue(report.category) === normalizeFilterValue(selectedTypeLabel);
    const queryMatches = !searchQuery.trim() || text.toLowerCase().includes(searchQuery.toLowerCase());

    return sectorMatches && typeMatches && queryMatches;
  });

  const visibleReportCount = filteredSubscriptionReports.length + filteredReports.length;

  const getRoleLabel = (role) => {
    const labels = {
      'FREE_USER': 'Free User',
      'BASIC_USER': 'Basic Plan',
      'PREMIUM_USER': 'Premium Plan',
      'ADMIN_USER': 'Admin'
    };
    return labels[role] || 'Free User';
  };

  return (
    <>
      <PageMeta
        title="Equity Intelligence | The B Tree"
        description="Track company earnings, valuation signals, market structure, and deep equity research across sectors and themes with The B Tree."
        canonical="https://www.thebtree.com/equity-intelligence"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <div className="min-h-screen bg-[#F7F5F2]">
      <main>
        {/* Page Header */}
        <div className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl md:text-5xl font-[Cormorant_Garamond] font-medium text-slate-900 mb-2">
                  Equity Intelligence™
                </h1>
                <p className="text-lg text-slate-600">
                  Companies, financials, valuations, risks, catalysts and long-term investment intelligence.
                </p>
              </div>
            </div>
          </div>
        </div>

        <ReportToolbar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search companies, reports, sectors..."
          reportCount={visibleReportCount}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          testId="equity-intelligence"
          filterContent={(
            <select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)} className="h-10 w-64 max-w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 cursor-pointer">
              {sectors.map((sector) => <option key={sector} value={toOptionValue(sector)}>{sector}</option>)}
            </select>
          )}
        />

        {/* Reports Grid/List */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          {/* Subscription Reports */}
          {subscriptionReportsError && (
            <div className="mb-8 bg-white border border-red-200 rounded-2xl p-6 text-red-700">
              {subscriptionReportsError}
            </div>
          )}

          {filteredSubscriptionReports.length > 0 ? (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-[Cormorant_Garamond] font-medium text-slate-900">
                  Reports
                </h2>
              </div>

                  <div className={viewMode === 'list' ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
                {filteredSubscriptionReports.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => navigate(`/subscription-reports/${r.id}`)}
                    className="group bg-white border border-slate-300 rounded-2xl p-6 shadow-sm hover:border-[#D4AF37] hover:shadow-lg transition-all duration-300 cursor-pointer relative"
                  >
                    <div className="flex-1 mb-4 min-w-0">
                      <div className="mb-2 flex items-start justify-between gap-4">
                        <h3 className="min-w-0 break-words [overflow-wrap:anywhere] text-xl font-[Cormorant_Garamond] font-semibold text-slate-950 group-hover:text-[#A77F16] transition-colors">
                          {r.title}
                        </h3>
                        <ReportIcon report={r} />
                      </div>
                      <p className="text-sm font-medium text-slate-700">{r.category} • {(r.totalPages ?? r.total_pages)} pages</p>
                      {(r.primaryFilter || r.secondaryFilter) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {r.primaryFilter && <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900">Sector: {r.primaryFilter}</span>}
                          {r.secondaryFilter && <span className="rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-800">Report Type: {r.secondaryFilter}</span>}
                        </div>
                      )}
                      {r.description && (
                        <p className="mt-4 rounded-lg border-l-4 border-[#D4AF37] bg-amber-50/80 px-3 py-2 text-[15px] font-medium leading-relaxed text-slate-800 line-clamp-3 shadow-sm">
                          {r.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-xs font-medium text-slate-600">Subscription Report</span>
                      <button className="flex items-center gap-1 text-sm font-semibold text-[#A77F16] hover:text-[#8F6A10] transition-colors">
                        View Report
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-10 bg-white border border-slate-200 rounded-2xl p-6 text-slate-700">
              No reports available in Research.
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-2 border-[#C8A84B] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {filteredReports.length > 0 && (
                <>
                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                    {filteredReports.map((report) => (
                      <div
                        key={report._id}
                        className="group bg-white border border-slate-300 rounded-2xl p-6 shadow-sm hover:border-[#D4AF37] hover:shadow-lg transition-all duration-300 cursor-pointer relative"
                      >
                        {/* Premium Badge */}
                        {report.premium_only && (
                          <div className="absolute top-4 right-4">
                            <Lock className="w-4 h-4 text-amber-500" />
                          </div>
                        )}

                        {/* Header */}
                        <div className="flex-1 mb-4 min-w-0">
                          <div className="mb-2 flex items-start justify-between gap-4">
                            <h3 className="min-w-0 break-words [overflow-wrap:anywhere] text-xl font-[Cormorant_Garamond] font-semibold text-slate-950 group-hover:text-[#A77F16] transition-colors">
                              {report.title}
                            </h3>
                            <ReportIcon report={report} />
                          </div>
                          <p className="text-sm font-medium text-slate-700">{report.category}</p>
                        </div>

                        {/* Tags */}
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          {report.tags && report.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs font-semibold tracking-widest uppercase text-slate-700 px-2 py-1 bg-slate-50 border border-slate-200 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>

                        {/* Preview */}
                        <p className="rounded-lg border-l-4 border-[#D4AF37] bg-amber-50/80 px-3 py-2 text-[15px] font-medium text-slate-800 leading-relaxed mb-4 line-clamp-3 shadow-sm">
                          {report.preview_content}
                        </p>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                          <span className="text-xs font-medium text-slate-600">Premium Report</span>
                          <button className="flex items-center gap-1 text-sm font-semibold text-[#A77F16] hover:text-[#8F6A10] transition-colors">
                            View Report
                            <ArrowUpRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Page {pagination.page} of {pagination.total_pages || 1}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="border-slate-200 hover:border-[#D4AF37]"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <span className="px-4 py-2 bg-slate-50 rounded-lg text-sm font-medium text-slate-700">
                        {pagination.page}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= Math.min(pagination.max_accessible_pages, pagination.total_pages || 1)}
                        className="border-slate-200 hover:border-[#D4AF37]"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {/* Upgrade Prompt */}
              {pagination.page >= pagination.max_accessible_pages && pagination.total_pages > pagination.max_accessible_pages && (
                <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-8 text-center">
                  <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-[Cormorant_Garamond] font-medium text-slate-900 mb-2">
                    Upgrade to Access All Reports
                  </h3>
                  <p className="text-slate-600 mb-6">
                    You've reached your free tier limit. Upgrade to Basic or Premium to access {pagination.total_pages - pagination.max_accessible_pages} more pages of research.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-semibold">
                      Upgrade to Basic
                    </Button>
                    <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                      Upgrade to Premium
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
    </>
  );
};

export default ResearchDashboard;
